<script setup lang="ts">
import FadeSlideTransition from '@shared/animation/transitions/FadeSlideTransition/FadeSlideTransition.vue';
import { useScrollLock } from '@shared/layout/useScrollLock';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import { useApplicationRoot } from '@tools/applicationRoot';
import { useId } from '@tools/composables/id';

const {
  title,
  isActive,
  hideCloseButton = false,
  noBodyPadding = false,
} = defineProps<{
  title: string;
  isActive: boolean;
  hideCloseButton?: boolean;
  noBodyPadding?: boolean;
}>();

const emit = defineEmits<{ close: [value?: Event] }>();
const applicationroot = useApplicationRoot();
const titleId = useId('classic-dialog-title');

useScrollLock(() => isActive);
</script>

<template>
  <Teleport :to="applicationroot">
    <div
      :class="[
        'fixed inset-0 z-overlay bg-[#000]/50 transition-opacity duration-500',
        { 'pointer-events-none opacity-0': !isActive },
      ]"
    ></div>
    <FadeSlideTransition direction="down">
      <div
        v-if="isActive"
        :aria-labelledby="titleId"
        class="fixed inset-0 z-overlay font-sans text-[16px] font-normal text-black !duration-400"
        role="dialog"
      >
        <!-- dialog container -->
        <div class="mx-auto my-7 w-fit md:w-[820px]">
          <div class="relative rounded bg-white shadow">
            <div
              class="flex w-full flex-col [&>*:not(:first-child)]:border-t [&>*:not(:first-child)]:border-grey-200"
            >
              <!-- Header -->
              <div class="flex w-full items-center justify-between px-8 py-6">
                <VHeading :level="1" appearance="2xl" :id="titleId">{{ title }}</VHeading>
                <button
                  @click="(e: any) => emit('close', e)"
                  type="button"
                  aria-label="Sluit dialog"
                >
                  <FontAwesomeIcon
                    v-if="!hideCloseButton"
                    class="size-6 cursor-pointer p-2 transition-colors hover:text-red-400"
                    :icon="['fas', 'xmark']"
                  />
                </button>
              </div>

              <!-- body -->
              <section
                class="max-h-[calc(100vh-20rem)] w-full overflow-y-auto"
                :class="{ 'p-8': !noBodyPadding }"
              >
                <slot></slot>
              </section>

              <!-- Footer -->
              <footer
                v-if="$slots.footer"
                class="flex w-full items-start justify-end gap-4 px-8 py-6 max-[820px]:flex-wrap max-[820px]:justify-start"
              >
                <slot name="footer"></slot>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </FadeSlideTransition>
  </Teleport>
</template>
